import { ACCESS_LEVELS, type AccessLevel } from '@/composables/useAccessControl'
import type { AccessOverviewResponse } from '@/services/access.service'
import { bareSubjectKey } from '@/utils/subjectKey'
import { featureRoleAliases } from '@/utils/featureRoleAliases'

// bareSubjectKey comes from utils/subjectKey.ts, NOT services/access.service.ts, so that
// pinia/access.ts (which imports this file) never statically pulls in the real access.service.ts
// module -- access.ts sits in the chain src/test.init.ts loads eagerly, before any per-test
// `vi.mock('@/services/access.service', ...)` can intercept it, which silently broke several
// specs' mocks the first time this went through a real service import (ZDB-7214). The
// `AccessOverviewResponse` type import above is erased at build time (no runtime import), so it's
// safe on its own.
//
// Shared by useAccess.ts and pinia/access.ts (ZDB-7214): the highest access-service RoleLevel the
// caller holds for a legacy-style feature type (e.g. 'CORE', 'CUSTOM_ATTRIBUTES'), expressed as an
// index into ACCESS_LEVELS' 4-slot ordering (READER/EDITOR/CREATOR/ADMIN) so it drops straight
// into the same comparisons the legacy useAccessControl() ladder already does. The new backend
// never grants CREATOR (see useOrgMemberAccess.ts), so that slot is simply never reached here --
// requiring CREATOR ends up requiring ADMIN, which is the safe/conservative reading until product
// decides a per-feature CREATOR mapping.
export function maxAccessIndexForSubject(
  overview: AccessOverviewResponse | undefined,
  featureType: string
): number {
  const holdings = overview?.holdings ?? []
  const aliases = new Set(featureRoleAliases(featureType).map((key) => key.toUpperCase()))

  const levels = holdings
    .flatMap((holding) => holding.max)
    .filter((entry) => aliases.has(bareSubjectKey(entry.subject).toUpperCase()))
    .map((entry) => {
      const index = ACCESS_LEVELS.indexOf(entry.level as AccessLevel)
      if (index === -1) {
        console.warn(
          `Unmapped access-service level "${entry.level}" for subject "${entry.subject}"`
        )
      }
      return index
    })

  return Math.max(...levels, -1)
}

// Whether any held subject's bare key starts with `prefix` -- backs hasZoneAccess's legacy
// `featureAccess.some(f => f.startsWith('FEAT_ZONE'))` check, which has no single feature type to
// look up (zones are keyed per-category, not one subject).
export function hasSubjectPrefix(overview: AccessOverviewResponse | undefined, prefix: string) {
  const holdings = overview?.holdings ?? []
  return holdings.some((holding) =>
    holding.max.some((entry) => bareSubjectKey(entry.subject).toUpperCase().startsWith(prefix))
  )
}
